// mindmetal-app/web/src/pages/employee/mindmetal/ExportTab.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Download,
  Search,
  CheckSquare,
  Square,
  FileJson,
  FileSpreadsheet,
  Package,
} from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Badge,
  EmptyState,
  api,
} from '@personic/shared'

interface ProfileSummary {
  profile_id: string
  canonical_id: string
  coverage_level: string
  observation_count: number
  tenant_name: string
  is_active: boolean
}

interface ProfilesResponse {
  profiles: ProfileSummary[]
  total: number
}

type ExportFormat = 'json' | 'csv'

export function ExportTab() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json')

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value)
    // Simple debounce using setTimeout
    clearTimeout((window as any).__exportSearchTimer)
    ;(window as any).__exportSearchTimer = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  // Fetch profiles
  const { data, isLoading } = useQuery({
    queryKey: ['mindmetal-export-profiles', debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (debouncedSearch) params.set('search', debouncedSearch)
      const { data } = await api.get<ProfilesResponse>(
        `/admin/mindmetal/profiles${params.toString() ? `?${params}` : ''}`
      )
      return data
    },
  })

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async ({
      profile_ids,
      format,
    }: {
      profile_ids: string[]
      format: ExportFormat
    }) => {
      const response = await api.post(
        '/admin/mindmetal/profiles/export',
        { profile_ids, format },
        { responseType: 'blob' }
      )
      return response.data as Blob
    },
    onSuccess: (blob) => {
      const extension = exportFormat === 'csv' ? 'csv' : 'json'
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mindmetal-profiles-${new Date().toISOString().slice(0, 10)}.${extension}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
  })

  const profiles = data?.profiles || []

  const handleToggle = (profileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(profileId)) {
        next.delete(profileId)
      } else {
        next.add(profileId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setSelectedIds(new Set(profiles.map((p) => p.profile_id)))
  }

  const handleDeselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleExport = () => {
    if (selectedIds.size === 0) return
    exportMutation.mutate({
      profile_ids: Array.from(selectedIds),
      format: exportFormat,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-copper-600" />
            Bulk Profile Export
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-warm-500 mb-6">
            Search and select profiles for bulk export. Choose your preferred format and download.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Search */}
            <Input
              label="Search Profiles"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by canonical ID, tenant..."
              leftIcon={<Search className="w-4 h-4" />}
            />

            {/* Format selector */}
            <div>
              <label className="block text-sm font-heading font-medium text-midnight-700 mb-2">
                Export Format
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setExportFormat('json')}
                  className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                    exportFormat === 'json'
                      ? 'border-copper-500 bg-copper-50'
                      : 'border-warm-200 hover:border-warm-300'
                  }`}
                >
                  <FileJson
                    className={`w-5 h-5 ${
                      exportFormat === 'json' ? 'text-copper-600' : 'text-warm-400'
                    }`}
                  />
                  <div>
                    <div className="font-medium text-midnight-800 text-sm">JSON</div>
                    <div className="text-xs text-warm-500">Full profile data</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setExportFormat('csv')}
                  className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                    exportFormat === 'csv'
                      ? 'border-copper-500 bg-copper-50'
                      : 'border-warm-200 hover:border-warm-300'
                  }`}
                >
                  <FileSpreadsheet
                    className={`w-5 h-5 ${
                      exportFormat === 'csv' ? 'text-copper-600' : 'text-warm-400'
                    }`}
                  />
                  <div>
                    <div className="font-medium text-midnight-800 text-sm">CSV</div>
                    <div className="text-xs text-warm-500">Spreadsheet-ready</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection controls + Export action */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleSelectAll} disabled={profiles.length === 0}>
            <CheckSquare className="w-4 h-4 mr-1.5" />
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedIds.size === 0}
          >
            <Square className="w-4 h-4 mr-1.5" />
            Deselect All
          </Button>
          {selectedIds.size > 0 && (
            <Badge variant="copper" size="sm">
              {selectedIds.size} selected
            </Badge>
          )}
        </div>

        <Button
          onClick={handleExport}
          disabled={selectedIds.size === 0 || exportMutation.isPending}
          isLoading={exportMutation.isPending}
          leftIcon={<Download className="w-4 h-4" />}
        >
          Export Selected
        </Button>
      </div>

      {/* Export error */}
      {exportMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          Export failed:{' '}
          {(exportMutation.error as any)?.response?.data?.detail ||
            (exportMutation.error as Error).message}
        </div>
      )}

      {/* Export success */}
      {exportMutation.isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-sage-50 border border-sage-200 rounded-xl text-sm text-sage-700 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export downloaded successfully
        </motion.div>
      )}

      {/* Profile list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
        </div>
      ) : profiles.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search className="w-8 h-8" />}
            title="No Profiles Found"
            description={
              debouncedSearch
                ? `No profiles matching "${debouncedSearch}". Try adjusting your search.`
                : 'No profiles available for export.'
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {profiles.map((profile, i) => {
            const isSelected = selectedIds.has(profile.profile_id)

            return (
              <motion.div
                key={profile.profile_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Card
                  variant="outlined"
                  padding="none"
                  interactive
                  onClick={() => handleToggle(profile.profile_id)}
                  className={`cursor-pointer transition-all ${
                    isSelected
                      ? 'border-copper-400 bg-copper-50/50'
                      : 'hover:border-warm-300'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Checkbox indicator */}
                      <div
                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-copper-500 text-white'
                            : 'border-2 border-warm-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>

                      {/* Profile info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-copper-600">
                            {profile.canonical_id}
                          </span>
                          {!profile.is_active && (
                            <Badge variant="default" size="sm">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-warm-500 mt-0.5">
                          {profile.tenant_name}
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-xs text-warm-500">Observations</div>
                          <div className="text-sm font-semibold text-midnight-800">
                            {profile.observation_count}
                          </div>
                        </div>
                        <Badge
                          variant={
                            profile.coverage_level === 'mature'
                              ? 'sage'
                              : profile.coverage_level === 'established'
                                ? 'success'
                                : profile.coverage_level === 'emerging'
                                  ? 'warning'
                                  : 'default'
                          }
                          size="sm"
                        >
                          {profile.coverage_level}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Total count */}
      {data && data.total > 0 && (
        <div className="text-center text-sm text-warm-500">
          Showing {profiles.length} of {data.total} profiles
        </div>
      )}
    </div>
  )
}
